import { router, Tabs, usePathname, useRouter, useSegments, RouteSegments, Route } from 'expo-router'
import { StyleSheet, View } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { observer } from 'mobx-react-lite'

import { TabBarComponent } from '@/components/TabBar/TabBar'
import { DS } from '@/design-system'
import { useRemoteConfig } from '@/modules/remote-config'
import { useTabNavConfig } from '@/hooks/useTabNavConfig'
import { BottomTabBarProps } from '@react-navigation/bottom-tabs'
// TODO: update library to use same as expo router

import { translate } from '../../../app/i18n'
import { useEffect } from 'react'

const segmentMap = {
  playScreen: 'play',
  homeScreen: 'home',
  learningScreen: 'learning',
  readScreen: 'read',
  membershipScreen: 'membership',
  profileScreen: 'profile'
} as const

const TabsLayout = observer(() => {
  const router = useRouter()
  const pathname = usePathname()
  console.log('LOG: > TabsLayout > pathname:', pathname)
  const [firstSegment] = useSegments()

  const {
    config: { initialRoute },
    isAdminTabBar
  } = useTabNavConfig()
  const initialRouteName = initialRoute.replace('Screen', '')

  useEffect(() => {
    // The admin tab bar ignores tab_nav_config, so realtime updates can't disable its tabs
    if (isAdminTabBar) return

    const dispose = useRemoteConfig.subscribe((state) => {
      const shouldJump = Object.entries(segmentMap).find(([key, value]) => {
        const isEnabled = state.tab_nav_config.enabledTabs[key as keyof typeof segmentMap]
        return isEnabled === false && value === firstSegment
      })
      // If a realtime update is received, check if the current active tab is disabled, if so jump to the initial route
      if (shouldJump) {
        const jumpTo = `/${segmentMap[state.tab_nav_config.initialRoute]}` as const
        router.replace(jumpTo)
      }
    })

    return () => {
      dispose()
    }
  }, [firstSegment, router, isAdminTabBar])

  return (
    <Tabs
      initialRouteName={initialRouteName}
      screenOptions={{
        headerShown: false,
        lazy: true
      }}
      safeAreaInsets={{
        top: 0,
        right: 0,
        bottom: 0,
        left: 0
      }}
      tabBar={(props: BottomTabBarProps) => <BottomTabBarComponent {...props} />}
    >
      <Tabs.Screen
        name="index"
        options={{
          href: null
        }}
      />
      <Tabs.Screen
        name="home"
        options={{
          title: translate('bottomNavigation.home')
        }}
      />
      <Tabs.Screen name="play" options={{ title: translate('bottomNavigation.play') }} />
      <Tabs.Screen name="read" options={{ title: translate('bottomNavigation.read') }} />
      <Tabs.Screen name="learning" options={{ title: translate('bottomNavigation.learn') }} />
      <Tabs.Screen name="profile" options={{ title: translate('bottomNavigation.profile') }} />
    </Tabs>
  )
})

const BottomTabBarComponent = (props: BottomTabBarProps) => {
  const {
    config: { enabledTabs }
  } = useTabNavConfig()
  const insets = useSafeAreaInsets()
  // A single enabled tab means no tab bar; the user stays on the tab_nav_config initial route
  const showTabnav = Object.values(enabledTabs).filter((value) => value).length > 1
  if (!showTabnav) {
    return null
  }
  return (
    <View style={[styles.tabBarContainer, { paddingBottom: DS.spacing[8] + insets.bottom }]}>
      <View style={[styles.tabBarStyle, containerStyles.webWrapper]}>
        <TabBarComponent {...props} />
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  tabBarContainer: {
    backgroundColor: DS.palette['Brand/Cream'],
    paddingHorizontal: DS.spacing[16],
    paddingTop: DS.spacing[8]
  },
  tabBarStyle: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center'
  }
})

const containerStyles = StyleSheet.create({
  webWrapper: {
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center'
  }
})

export default TabsLayout
